<style lang="scss" scoped>
@import "../assets/bag-label.scss";

.circle {
  padding: 0;
  border: 4px solid black;
  height: 70px;
  border-radius: 50%;
  width: 70px;
  font-size: 50px;
  line-height: 60px;
  font-weight: bolder;
}

// the logo, box count and QR code each fill the same height and centre their
// contents, so the whitespace above and below is even across all three
.qr-row-item {
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block; // no inline baseline gap under the image
  }
}

// show the sticker's edge on screen only. outline rather than border so it
// takes up no space, and print drops it entirely.
.page-break.full-page-portrait {
  outline: 1px solid #bbb;

  @media print {
    outline: none;
  }
}

// the box count circle sets the width of its own column and of the logo
// column, so the two narrow columns either side of the QR code match
$box-count-size: 64px;

// the logo is inset from the top and bottom of the band. the row is
// no-gutters, which forces padding:0!important onto the column, so the inset
// is taken out of the image height and shared evenly by the flex centring.
.logo-col {
  min-width: $box-count-size;

  img {
    height: calc(100% - 7mm);
    width: auto;
  }
}

// second copy of the box count, sat between the QR code and the logo.
// the label is a fixed 7.6cm, so this column is capped and clipped to
// guarantee it can never push the sticker wider.
.box-count-col {
  max-width: 70px;
  overflow: hidden;

  .box-count-of {
    font-size: 12px;
    line-height: 1.1;
  }

  .circle-small {
    border-width: 3px;
    height: $box-count-size;
    width: $box-count-size;
    font-size: 40px;
    line-height: 58px;
    margin: 0 auto;
  }
}
</style>
<template>
  <v-container fluid class="pa-0 fed-bag-label full-page-portrait ">
    <v-container class="page-break full-page-portrait ma-0 pa-0 fill-height">
      <v-row dense no-gutters>
        <v-col>
          <div class="pt-3 customer-name"> {{ customerName }}</div>
        </v-col>
      </v-row>
      <!-- all three columns are content sized and spread with space-between.
           each column is the height of the QR code with its contents centred,
           so all three share the same band of whitespace above and below.
           the box count always holds the right hand column; the logo and the
           QR code alternate between the other two. -->
      <v-row dense no-gutters class="mb-0 pb-0 pr-1 align-center justify-space-between">
        <template v-if="customerName.length%2===0">
          <v-col cols="auto" class="pa-0 qr-row-item logo-col" :style="qrRowItemStyle">
            <img src="@/assets/FED_Logo03.svg"/>
          </v-col>
          <v-col cols="auto" class="pa-0 qr-row-item" :style="qrRowItemStyle">
            <div>
              <img :src="qrCodeDataURL" :style="`max-width:${qrCodeSize}px; max-height:${qrCodeSize}px;`"/>
            </div>
          </v-col>
        </template>
        <template v-if="customerName.length%2!==0">
          <v-col cols="auto" class="pa-0 qr-row-item" :style="qrRowItemStyle">
            <div>
              <img :src="qrCodeDataURL" :style="`max-width:${qrCodeSize}px; max-height:${qrCodeSize}px;`"/>
            </div>
          </v-col>
          <v-col cols="auto" class="pa-0 qr-row-item logo-col" :style="qrRowItemStyle">
            <img src="@/assets/FED_Logo03.svg"/>
          </v-col>
        </template>
        <!-- cols="auto" so the count never widens the label -->
        <v-col cols="auto" class="pa-0 box-count-col qr-row-item" :style="qrRowItemStyle">
          <div>
            <div class="box-count-of" v-if="`${bags}`!=='1'">{{ bag }} of</div>
            <div class="circle circle-small">{{ bagText }}</div>
          </div>
        </v-col>
      </v-row>
      <v-row dense no-gutters class="mb-0 pb-0 d-print-none">
        <v-col>
          <span class=""><br/>{{ qrCodeValue }}</span>
        </v-col>
      </v-row>
      <!--      <v-row>-->
      <!--        <v-col>-->
      <!--          <div class="pa-0 ma-0 customer-address" v-if="address_line_1">{{ address_line_1 }}</div>-->
      <!--          <div class="pa-0 ma-0 customer-address" v-if="address_line_2">{{ address_line_2 }}</div>-->
      <!--        </v-col>-->
      <!--      </v-row>-->
      <v-row no-gutters>
        <v-col align-self="end" class="text-left pl-1">
          <div class="pa-0 ma-0 customer-address" v-if="apt">#{{ apt }}</div>
          <div class="pa-0 ma-0 customer-address" v-if="address_line_1_no_apt">{{ address_line_1_no_apt }}</div>
          <div class="pa-0 ma-0 customer-address" v-if="address_line_2">{{ address_line_2 }}</div>
          <template v-if="bins && bins.length>0">
            <v-chip outlined small v-for="(bin,i) of bins" v-bind:key="i">{{ bin }}</v-chip>
          </template>
          <span style="font-size: 15px" class="mb-4">
            {{ driverInitials }}<span v-if="sequence">-{{ sequence }}</span>
            </span>
        </v-col>
        <v-col align-self="end" cols="3" class="mr-1">
          <template v-if="`${bags}`!=='1'">
            {{ bag }} of
            <br/>
          </template>
          <div class="circle">{{ bagText }}</div>
          <span v-if="deliveryDate" style="font-size: 12px; font-family: Roboto, sans-serif">{{ deliveryDate }}</span>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>
<script>

import BagCalc from "@/store/bag-calc";
import moment from "moment";

export default {
  name: 'BagLabel',
  components: {},
  props: {
    driver: String,
    customerName: String,
    bag: {},
    bags: {},
    bagSize: String,
    bins: {},
    sequence: {},
    qrCodeValue: {},
    address_line_1: String,
    address_line_2: String,
    eta: {}
  },
  async mounted() {
    if (this.qrCodeValue) {
      this.qrCodeDataURL = await BagCalc.generateQRCodes(this.qrCodeValue, {size: this.qrCodeSize});
    }
  },
  methods: {
    capitalise(str) {
      if (!str) {
        return '';
      }
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
  computed: {
    bagText() {
      return `${this.bags || ''}`
        + (this.bagSize === 'large' ? ' *' : '')
    },
    driverInitials() {
      return (this.driver || '').split(' ').map(str => str.charAt(0).toUpperCase()).join('')
    },
    apt() {
      const parts = this.address_line_1.split('-');
      if (parts.length > 1) {
        return parts[0];
      } else {
        return false;
      }
    },
    address_line_1_no_apt() {
      const parts = this.address_line_1.split('-');
      if (parts.length > 1) {
        return parts.splice(1).join('-');
      } else {
        return this.address_line_1;
      }
    },
    // the QR code is the tallest thing in the row, so it sets the band height
    // that the logo and the box count are centred within
    qrRowItemStyle() {
      return {height: `${this.qrCodeSize}px`};
    },
    deliveryDate() {
      if (this.eta) {
        return moment(this.eta).format('D/M/YY');
      } else
        return '';
    }
  },
  data() {
    return {
      qrCodeDataURL: null,
      qrCodeSize: 100
    }
  }
};
</script>
